// @core/stores/team/TeamStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { teamApi } from '@core/api';
import type {
  AcceptRoleDelegationRequest,
  AcceptTeamInvitationRequest,
  AcknowledgeScheduleRotationRequest,
  ActivateTeamAvatarRequest,
  AddScheduleParticipantRequest,
  AddTeamMemberRequest,
  ApproveTeamJoinApplicationRequest,
  ApproveTeamRoleApplicationRequest,
  AssignTeamPermissionToRoleRequest,
  AssignTeamRoleRequest,
  CancelRoleDelegationRequest,
  CancelTeamInvitationRequest,
  CancelTeamJoinApplicationRequest,
  CancelTeamOwnershipTransferRequest,
  CancelTeamRoleApplicationRequest,
  CancelTemporaryRoleAssignmentRequest,
  CreateRoleDelegationRequest,
  CreateRoleScheduleRequest,
  CreateTeamAvatarRequest,
  CreateTeamBannerRequest,
  CreateTeamInvitationRequest,
  CreateTeamJoinApplicationRequest,
  CreateTeamOwnershipTransferRequest,
  CreateTeamRequest,
  CreateTeamRoleApplicationRequest,
  CreateTeamRoleRequest,
  CreateTemporaryRoleAssignmentRequest,
  DeclineTeamInvitationRequest,
  ExtendRoleDelegationRequest,
  GetCurrentScheduleAssigneeResponse,
  GetTeamAvatarResponse,
  GetTeamBannerResponse,
  GetTeamJoinApplicationResponse,
  GetTeamOwnershipTransferResponse,
  GetTeamRoleApplicationResponse,
  GetTeamSecuritySettingsResponse,
  GetTeamUISettingsResponse,
  GetTeamUserSettingsResponse,
  GrantTeamOwnershipRequest,
  ListRoleAutomationEventsResponse,
  ListRoleDelegationsResponse,
  ListRoleSchedulesResponse,
  ListTeamAvatarsResponse,
  ListTeamInvitationsResponse,
  ListTeamJoinApplicationsResponse,
  ListTeamLogsResponse,
  ListTeamMembersResponse,
  ListTeamOwnershipTransfersResponse,
  ListTeamPermissionsResponse,
  ListTeamRoleApplicationsResponse,
  ListTeamRolesResponse,
  ListTeamUsersByRoleResponse,
  ListTemporaryRoleAssignmentsResponse,
  PauseRoleScheduleRequest,
  RejectRoleDelegationRequest,
  ResetTeamSecuritySettingsRequest,
  ResetTeamUISettingsRequest,
  ResetTeamUserSettingsRequest,
  ResumeRoleScheduleRequest,
  RevokeTeamOwnershipRequest,
  RevokeTeamRoleRequest,
  RoleDelegationResponse,
  RoleScheduleResponse,
  Team,
  TeamRoleResponse,
  TemporaryRoleAssignmentResponse,
  TransferTeamOwnershipRequest,
  TriggerScheduleRotationRequest,
  UpdateRoleDelegationRequest,
  UpdateRoleScheduleRequest,
  UpdateTeamMemberRoleRequest,
  UpdateTeamRequest,
  UpdateTeamRoleRequest,
  UpdateTeamSecuritySettingsRequest,
  UpdateTeamUISettingsRequest,
  UpdateTeamUserRoleRequest,
  UpdateTeamUserSettingsRequest,
  UpdateTemporaryRoleAssignmentRequest,
} from '@core/types/team';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class TeamStore {
  // List state
  items: Team[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Team | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Team>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Team | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Team | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Accept delegation as delegate
   */
  async AcceptRoleDelegation(team_id: string, delegation_id: string, data: AcceptRoleDelegationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.AcceptRoleDelegation(team_id, delegation_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Accept team invitation
   */
  async AcceptTeamInvitation(invitation_id: string, data: AcceptTeamInvitationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.AcceptTeamInvitation(invitation_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Acknowledge rotation handoff
   */
  async AcknowledgeScheduleRotation(team_id: string, schedule_id: string, data: AcknowledgeScheduleRotationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.AcknowledgeScheduleRotation(team_id, schedule_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Set active team avatar
   */
  async ActivateTeamAvatar(team_id: string, avatar_id: string, data: ActivateTeamAvatarRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ActivateTeamAvatar(team_id, avatar_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Add participant to schedule
   */
  async AddScheduleParticipant(team_id: string, schedule_id: string, data: AddScheduleParticipantRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.AddScheduleParticipant(team_id, schedule_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Add team member
   */
  async AddTeamMember(team_id: string, data: AddTeamMemberRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.AddTeamMember(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Approve join application
   */
  async ApproveTeamJoinApplication(team_id: string, application_id: string, data: ApproveTeamJoinApplicationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ApproveTeamJoinApplication(team_id, application_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Approve role application
   */
  async ApproveTeamRoleApplication(team_id: string, request_id: string, data: ApproveTeamRoleApplicationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ApproveTeamRoleApplication(team_id, request_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Assign permissions to role
   */
  async AssignTeamPermissionToRole(team_id: string, role_id: string, data: AssignTeamPermissionToRoleRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.AssignTeamPermissionToRole(team_id, role_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Assign role to team member
   */
  async AssignTeamRole(team_id: string, data: AssignTeamRoleRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.AssignTeamRole(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Cancel role delegation
   */
  async CancelRoleDelegation(team_id: string, delegation_id: string, data: CancelRoleDelegationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.CancelRoleDelegation(team_id, delegation_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Cancel team invitation
   */
  async CancelTeamInvitation(invitation_id: string, data: CancelTeamInvitationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.CancelTeamInvitation(invitation_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Cancel join application
   */
  async CancelTeamJoinApplication(application_id: string, data: CancelTeamJoinApplicationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.CancelTeamJoinApplication(application_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Cancel ownership transfer
   */
  async CancelTeamOwnershipTransfer(transfer_id: string, data: CancelTeamOwnershipTransferRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.CancelTeamOwnershipTransfer(transfer_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Cancel role application
   */
  async CancelTeamRoleApplication(request_id: string, data: CancelTeamRoleApplicationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.CancelTeamRoleApplication(request_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Cancel temporary role assignment
   */
  async CancelTemporaryRoleAssignment(team_id: string, assignment_id: string, data: CancelTemporaryRoleAssignmentRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.CancelTemporaryRoleAssignment(team_id, assignment_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create role delegation
   */
  async CreateRoleDelegation(team_id: string, data: CreateRoleDelegationRequest): Promise<RoleDelegationResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.CreateRoleDelegation(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create role schedule
   */
  async CreateRoleSchedule(team_id: string, data: CreateRoleScheduleRequest): Promise<RoleScheduleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.CreateRoleSchedule(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create a new team
   */
  async CreateTeam(data: CreateTeamRequest): Promise<Team> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await teamApi.CreateTeam(data);
      const item = response.team;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Upload team avatar
   */
  async CreateTeamAvatar(team_id: string, data: CreateTeamAvatarRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.CreateTeamAvatar(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Upload team banner
   */
  async CreateTeamBanner(team_id: string, data: CreateTeamBannerRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.CreateTeamBanner(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create team invitation
   */
  async CreateTeamInvitation(team_id: string, data: CreateTeamInvitationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.CreateTeamInvitation(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Request to join team
   */
  async CreateTeamJoinApplication(team_id: string, data: CreateTeamJoinApplicationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.CreateTeamJoinApplication(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Request team ownership transfer
   */
  async CreateTeamOwnershipTransfer(team_id: string, data: CreateTeamOwnershipTransferRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.CreateTeamOwnershipTransfer(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create custom team role
   */
  async CreateTeamRole(team_id: string, data: CreateTeamRoleRequest): Promise<TeamRoleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.CreateTeamRole(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Request role change
   */
  async CreateTeamRoleApplication(team_id: string, data: CreateTeamRoleApplicationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.CreateTeamRoleApplication(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create temporary role assignment
   */
  async CreateTemporaryRoleAssignment(team_id: string, data: CreateTemporaryRoleAssignmentRequest): Promise<TemporaryRoleAssignmentResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.CreateTemporaryRoleAssignment(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Decline team invitation
   */
  async DeclineTeamInvitation(invitation_id: string, data: DeclineTeamInvitationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.DeclineTeamInvitation(invitation_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete role schedule
   */
  async DeleteRoleSchedule(team_id: string, schedule_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await teamApi.DeleteRoleSchedule(team_id, schedule_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== schedule_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === schedule_id) {
          this.currentItem = null;
        }

        this.removeFromCache(schedule_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete team
   */
  async DeleteTeam(team_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await teamApi.DeleteTeam(team_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== team_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === team_id) {
          this.currentItem = null;
        }

        this.removeFromCache(team_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete team avatar
   */
  async DeleteTeamAvatar(team_id: string, avatar_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await teamApi.DeleteTeamAvatar(team_id, avatar_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== avatar_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === avatar_id) {
          this.currentItem = null;
        }

        this.removeFromCache(avatar_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete team banner
   */
  async DeleteTeamBanner(team_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await teamApi.DeleteTeamBanner(team_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== team_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === team_id) {
          this.currentItem = null;
        }

        this.removeFromCache(team_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete team role
   */
  async DeleteTeamRole(team_id: string, role_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await teamApi.DeleteTeamRole(team_id, role_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== role_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === role_id) {
          this.currentItem = null;
        }

        this.removeFromCache(role_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Extend delegation period
   */
  async ExtendRoleDelegation(team_id: string, delegation_id: string, data: ExtendRoleDelegationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ExtendRoleDelegation(team_id, delegation_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get current schedule assignee
   */
  async GetCurrentScheduleAssignee(team_id: string, schedule_id: string): Promise<GetCurrentScheduleAssigneeResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.GetCurrentScheduleAssignee(team_id, schedule_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get role delegation
   */
  async GetRoleDelegation(team_id: string, delegation_id: string): Promise<RoleDelegationResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.GetRoleDelegation(team_id, delegation_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get role schedule
   */
  async GetRoleSchedule(team_id: string, schedule_id: string): Promise<RoleScheduleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.GetRoleSchedule(team_id, schedule_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get team by ID
   */
  async GetTeam(team_id: string): Promise<Team> {
    // Check cache first
    const cached = this.cache.get(team_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await teamApi.GetTeam(team_id);
      const item = response.team;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get team avatar
   */
  async GetTeamAvatar(team_id: string, avatar_id: string): Promise<GetTeamAvatarResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.GetTeamAvatar(team_id, avatar_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get team banner
   */
  async GetTeamBanner(team_id: string): Promise<GetTeamBannerResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.GetTeamBanner(team_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get detailed team information
   */
  async GetTeamDetails(team_id: string): Promise<Team> {
    // Check cache first
    const cached = this.cache.get(team_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await teamApi.GetTeamDetails(team_id);
      const item = response.team;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get join application details
   */
  async GetTeamJoinApplication(application_id: string): Promise<GetTeamJoinApplicationResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.GetTeamJoinApplication(application_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get ownership transfer details
   */
  async GetTeamOwnershipTransfer(transfer_id: string): Promise<GetTeamOwnershipTransferResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.GetTeamOwnershipTransfer(transfer_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get role application details
   */
  async GetTeamRoleApplication(request_id: string): Promise<GetTeamRoleApplicationResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.GetTeamRoleApplication(request_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get team security settings
   */
  async GetTeamSecuritySettings(team_id: string): Promise<GetTeamSecuritySettingsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.GetTeamSecuritySettings(team_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get team UI settings
   */
  async GetTeamUISettings(team_id: string): Promise<GetTeamUISettingsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.GetTeamUISettings(team_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get team user settings
   */
  async GetTeamUserSettings(team_id: string): Promise<GetTeamUserSettingsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.GetTeamUserSettings(team_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get temporary role assignment
   */
  async GetTemporaryRoleAssignment(team_id: string, assignment_id: string): Promise<TemporaryRoleAssignmentResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.GetTemporaryRoleAssignment(team_id, assignment_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Grant team ownership
   */
  async GrantTeamOwnership(team_id: string, data: GrantTeamOwnershipRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.GrantTeamOwnership(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List delegations assigned to me
   */
  async ListDelegatedToMe(team_id: string, params?: PaginatedRequest): Promise<ListRoleDelegationsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ListDelegatedToMe(team_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List delegations I created
   */
  async ListMyDelegations(team_id: string, params?: PaginatedRequest): Promise<ListRoleDelegationsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ListMyDelegations(team_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List my team invitations
   */
  async ListMyTeamInvitations(params?: PaginatedRequest): Promise<ListTeamInvitationsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ListMyTeamInvitations(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List my temporary roles
   */
  async ListMyTemporaryRoles(team_id: string, params?: PaginatedRequest): Promise<ListTemporaryRoleAssignmentsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ListMyTemporaryRoles(team_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List role automation events
   */
  async ListRoleAutomationEvents(team_id: string, params?: PaginatedRequest): Promise<ListRoleAutomationEventsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ListRoleAutomationEvents(team_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List role delegations
   */
  async ListRoleDelegations(team_id: string, params?: PaginatedRequest): Promise<ListRoleDelegationsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ListRoleDelegations(team_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List role schedules
   */
  async ListRoleSchedules(team_id: string, params?: PaginatedRequest): Promise<ListRoleSchedulesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ListRoleSchedules(team_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List team avatars
   */
  async ListTeamAvatars(team_id: string, params?: PaginatedRequest): Promise<ListTeamAvatarsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ListTeamAvatars(team_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List team invitations
   */
  async ListTeamInvitations(team_id: string, params?: PaginatedRequest): Promise<ListTeamInvitationsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ListTeamInvitations(team_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List join applications
   */
  async ListTeamJoinApplications(team_id: string, params?: PaginatedRequest): Promise<ListTeamJoinApplicationsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ListTeamJoinApplications(team_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List team activity logs
   */
  async ListTeamLogs(team_id: string, params?: PaginatedRequest): Promise<ListTeamLogsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ListTeamLogs(team_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List team members
   */
  async ListTeamMembers(team_id: string, params?: PaginatedRequest): Promise<ListTeamMembersResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ListTeamMembers(team_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List ownership transfers
   */
  async ListTeamOwnershipTransfers(team_id: string, params?: PaginatedRequest): Promise<ListTeamOwnershipTransfersResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ListTeamOwnershipTransfers(team_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List role permissions
   */
  async ListTeamPermissions(team_id: string, role_id: string, params?: PaginatedRequest): Promise<ListTeamPermissionsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ListTeamPermissions(team_id, role_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List role applications
   */
  async ListTeamRoleApplications(team_id: string, params?: PaginatedRequest): Promise<ListTeamRoleApplicationsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ListTeamRoleApplications(team_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List team roles
   */
  async ListTeamRoles(team_id: string, params?: PaginatedRequest): Promise<ListTeamRolesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ListTeamRoles(team_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List users with specific role
   */
  async ListTeamUsersByRole(team_id: string, role_id: string, params?: PaginatedRequest): Promise<ListTeamUsersByRoleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ListTeamUsersByRole(team_id, role_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List teams with full details
   */
  async ListTeams(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await teamApi.ListTeams(params);

      runInAction(() => {
        this.items = response.teams?.teams ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List temporary role assignments
   */
  async ListTemporaryRoleAssignments(team_id: string, params?: PaginatedRequest): Promise<ListTemporaryRoleAssignmentsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ListTemporaryRoleAssignments(team_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Pause role schedule
   */
  async PauseRoleSchedule(team_id: string, schedule_id: string, data: PauseRoleScheduleRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.PauseRoleSchedule(team_id, schedule_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Reject delegation as delegate
   */
  async RejectRoleDelegation(team_id: string, delegation_id: string, data: RejectRoleDelegationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.RejectRoleDelegation(team_id, delegation_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Remove participant from schedule
   */
  async RemoveScheduleParticipant(team_id: string, schedule_id: string, user_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.RemoveScheduleParticipant(team_id, schedule_id, user_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Remove team member
   */
  async RemoveTeamMember(team_id: string, member_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.RemoveTeamMember(team_id, member_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Remove permissions from role
   */
  async RemoveTeamPermissionFromRole(team_id: string, role_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.RemoveTeamPermissionFromRole(team_id, role_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Reset team security settings
   */
  async ResetTeamSecuritySettings(team_id: string, data: ResetTeamSecuritySettingsRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ResetTeamSecuritySettings(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Reset team UI settings
   */
  async ResetTeamUISettings(team_id: string, data: ResetTeamUISettingsRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ResetTeamUISettings(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Reset team user settings
   */
  async ResetTeamUserSettings(team_id: string, data: ResetTeamUserSettingsRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ResetTeamUserSettings(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Resume role schedule
   */
  async ResumeRoleSchedule(team_id: string, schedule_id: string, data: ResumeRoleScheduleRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.ResumeRoleSchedule(team_id, schedule_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Revoke team ownership
   */
  async RevokeTeamOwnership(team_id: string, data: RevokeTeamOwnershipRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.RevokeTeamOwnership(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Revoke role from team member
   */
  async RevokeTeamRole(team_id: string, data: RevokeTeamRoleRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.RevokeTeamRole(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Transfer team ownership
   */
  async TransferTeamOwnership(team_id: string, data: TransferTeamOwnershipRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.TransferTeamOwnership(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Trigger immediate rotation
   */
  async TriggerScheduleRotation(team_id: string, schedule_id: string, data: TriggerScheduleRotationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.TriggerScheduleRotation(team_id, schedule_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update role delegation
   */
  async UpdateRoleDelegation(team_id: string, delegation_id: string, data: UpdateRoleDelegationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.UpdateRoleDelegation(team_id, delegation_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update role schedule
   */
  async UpdateRoleSchedule(team_id: string, schedule_id: string, data: UpdateRoleScheduleRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.UpdateRoleSchedule(team_id, schedule_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update team information
   */
  async UpdateTeam(team_id: string, data: UpdateTeamRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.UpdateTeam(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update team member role
   */
  async UpdateTeamMemberRole(team_id: string, member_id: string, data: UpdateTeamMemberRoleRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.UpdateTeamMemberRole(team_id, member_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update team role
   */
  async UpdateTeamRole(team_id: string, role_id: string, data: UpdateTeamRoleRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.UpdateTeamRole(team_id, role_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update team security settings
   */
  async UpdateTeamSecuritySettings(team_id: string, data: UpdateTeamSecuritySettingsRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.UpdateTeamSecuritySettings(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update team UI settings
   */
  async UpdateTeamUISettings(team_id: string, data: UpdateTeamUISettingsRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.UpdateTeamUISettings(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update user role assignment
   */
  async UpdateTeamUserRole(team_id: string, user_id: string, data: UpdateTeamUserRoleRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.UpdateTeamUserRole(team_id, user_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update team user settings
   */
  async UpdateTeamUserSettings(team_id: string, data: UpdateTeamUserSettingsRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.UpdateTeamUserSettings(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update temporary role assignment
   */
  async UpdateTemporaryRoleAssignment(team_id: string, assignment_id: string, data: UpdateTemporaryRoleAssignmentRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await teamApi.UpdateTemporaryRoleAssignment(team_id, assignment_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const teamStore = new TeamStore();
